import { Box, Center, Spinner, Text, VStack } from '@chakra-ui/react'
import {
  FileDBValue,
  FullPagePlacement,
  InlineAttachmentPlacement,
  InputTemplateAttachment,
  isInlineAttachment,
  isSignableDocument,
  TemplateAttachment,
} from '@hb/shared'
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react'
import { Document, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { useResizeObserver } from '../../hooks/useResizeObserver'
import { PdfDocumentFormProvider } from '../RichText/DocumentFormProvider'
import { useExternalPdfTemplateEditor } from '../Templates/ExternalPdf/context'
import { PdfViewProvider, usePdfView } from './context'
import './styles.css'

import { useAuth, useScreen } from '@hb/react'
import { DocumentCallback } from 'react-pdf/dist/cjs/shared/types'
import { useFile } from '../../hooks/backend/storage/downloadFile'
import { DocumentDownloadButton } from '../Templates/DocumentDownloadButton'
import { EnterPassword } from './EnterPassword'
import { GoToNextField } from './GoToNextField'
import { usePdfDownload, usePdfPassword } from './hooks'
import { PageNumberIndicator } from './PageNumberIndicator'
import { PdfPage } from './PdfPage'
import { PdfViewAttachmentPage, PdfViewPage, PdfViewProps } from './types'
import { sortInputAttachments } from './utils'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

// const sortInputAttachments = (inputAttachments: InputTemplateAttachment[]) =>
//   inputAttachments.sort((a, b) => {
//     if (isInlineAttachment(a) && isInlineAttachment(b)) {
//       const pageDiff = a.placement.pageIndex - b.placement.pageIndex
//       if (pageDiff !== 0) {
//         return pageDiff
//       }
//       const heightDiff = Math.abs(
//         a.placement.orientation.position[1] - b.placement.orientation.position[1],
//       )
//       if (heightDiff < 10) {
//         return a.placement.orientation.position[0] > b.placement.orientation.position[0] ? 1 : -1
//       }
//       return a.placement.orientation.position[1] > b.placement.orientation.position[1] ? 1 : -1
//     }
//     if (isInlineAttachment(a) && !isInlinePlacement(b.placement)) {
//       return b.placement.afterPageIndex >= a.placement.pageIndex ? 1 : -1
//     }
//     if (!isInlinePlacement(a.placement) && isInlineAttachment(b)) {
//       return a.placement.afterPageIndex >= b.placement.pageIndex ? 1 : -1
//     }
//     if (!isInlinePlacement(a.placement) && !isInlinePlacement(b.placement)) {
//       return a.placement.afterPageIndex - b.placement.afterPageIndex
//     }
//     return 0
//   })

// const PdfPage = ({
//   page,
//   index,
//   pageWidth,
//   viewWidth,
//   pageHeight,
// }: {
//   page: PdfViewPage
//   index: number
//   pageWidth: number
//   viewWidth: number
//   pageHeight: number
// }) => {
//   const pageRef = useRef<HTMLDivElement>(null)
//   const pageAspect = useMemo(() => pageWidth / pageHeight, [pageWidth, pageHeight])
//   const pageScale = useMemo(() => viewWidth / pageWidth, [viewWidth, pageWidth])
//   return (
//     <Box
//       ref={pageRef}
//       className="pdf-view-page-container"
//       key={`page_${index}`}
//       width={`${viewWidth}px`}
//       position="relative">
//       {page.type === 'file' ? (
//         <Box position="relative">
//           <Page
//             className="pdf-view-page"
//             loading={
//               <Center w={`${viewWidth}px`} h={`${pageHeight}px`}>
//                 <Spinner />
//               </Center>
//             }
//             width={viewWidth}
//             height={pageHeight * pageAspect}
//             pageNumber={index + 1}
//           />
//           <AnimatePresence>
//             {page.attachments.map(attachment => (
//               <AttachmentView
//                 pageHeight={pageHeight}
//                 pageScale={pageScale}
//                 pageWidth={pageWidth}
//                 key={attachment.id}
//                 attachment={attachment}
//               />
//             ))}
//           </AnimatePresence>
//         </Box>
//       ) : (
//         <AttachmentView
//           attachment={page.attachment}
//           pageScale={pageScale}
//           pageWidth={pageWidth}
//           pageHeight={pageHeight}
//         />
//       )}
//     </Box>
//   )
// }

const PdfLoading = ({ width, height }: { width: number; height: number }) => {
  const { isMobile } = useScreen()
  return (
    <Center flexFlow="column" w={`${width}px`} h={`${height}px`}>
      <Spinner />
      {isMobile ? (
        <Text>If the PDF doesn't load, please try to open it on a desktop or laptop.</Text>
      ) : null}
    </Center>
  )
}

const PdfViewBody = ({
  url,
  children,
  height,
  attachments,
  document,
  withDownload,
  onFormSubmit,
  readOnly,
  width,
  onClose,
}: PropsWithChildren<Omit<PdfViewProps, 'onAfterRegenerate'>>) => {
  const authUser = useAuth(s => s.authUser)
  const { updateNumPages, numPages, updatePageSize, pageSizes, modalWidth } = usePdfView()
  const { isMobile } = useScreen()
  const pdfPassword = usePdfPassword(onClose)

  // const width = useMemo(() => Math.max(500, widthProp), [widthProp])

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString()
  }, [])

  useEffect(() => {
    if (!url) updateNumPages(null)
  }, [url, updateNumPages])
  const onDocumentLoadSuccess = useCallback(
    (e: DocumentCallback) => {
      pdfPassword.clearRequestingPassword()
      for (let i = 1; i <= e.numPages; i++) {
        e.getPage(i).then(page => {
          updatePageSize(i, [page.view[2] - page.view[0], page.view[3] - page.view[1]])
        })
      }
      updateNumPages(e.numPages)
    },
    [updateNumPages, pdfPassword, updatePageSize],
  )

  const { onAttachmentSelect } = useExternalPdfTemplateEditor()
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { inlineAttachments, fullPageAttachments, inputAttachments } = useMemo(() => {
    const inline: TemplateAttachment<InlineAttachmentPlacement>[] = []
    const inputAttachments: InputTemplateAttachment<InlineAttachmentPlacement>[] = []
    const fullPage: TemplateAttachment<FullPagePlacement>[][] = Array.from(
      new Array(numPages ?? 0),
      () => [],
    )
    const isSignedConsentForm = document && isSignableDocument(document) && document.signedOn
    if (!attachments || isSignedConsentForm)
      return { inlineAttachments: inline, fullPageAttachments: fullPage, inputAttachments: [] }
    Object.values(attachments).forEach(a => {
      if (a.type === 'input') {
        inputAttachments.push(a as InputTemplateAttachment<InlineAttachmentPlacement>)
      }
      if (isInlineAttachment(a)) {
        inline.push(a)
      } else {
        const asFullPage = a as TemplateAttachment<FullPagePlacement>
        const placement = asFullPage.placement
        fullPage[placement.afterPageIndex + 1].push(asFullPage)
      }
    })
    return {
      inlineAttachments: inline,
      fullPageAttachments: fullPage,
      inputAttachments: sortInputAttachments(inputAttachments),
    }
  }, [attachments, numPages, document])

  const pages = useMemo<PdfViewPage[]>(() => {
    const p: PdfViewPage[] = Array.from(new Array(numPages ?? 0), (_, index) => ({
      type: 'file',
      pageNumber: index + 1,
      attachments: inlineAttachments
        .filter(a =>
          index === (numPages ?? 0) - 1
            ? a.placement.pageIndex >= index
            : a.placement.pageIndex === index,
        )
        .sort((a, b) => {
          const sameLine =
            Math.abs(a.placement.orientation.position[1] - b.placement.orientation.position[1]) < 10
          if (sameLine)
            return a.placement.orientation.position[0] > b.placement.orientation.position[0]
              ? 1
              : -1
          return a.placement.orientation.position[1] > b.placement.orientation.position[1] ? 1 : -1
        }),
    }))

    for (let i = fullPageAttachments.length - 1; i >= 0; i--) {
      const attachments = fullPageAttachments[i]
      const attachmentPages: PdfViewAttachmentPage[] = attachments.map(a => ({
        type: 'attachment',
        attachment: a,
      }))
      if (attachments.length > 0) {
        p.splice(i, 0, ...attachmentPages)
      }
    }

    return p
  }, [numPages, inlineAttachments, fullPageAttachments])

  const inputIds = useMemo(() => inputAttachments.map(a => a.id), [inputAttachments])

  const contentRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const { height: headerHeight } = useResizeObserver(headerRef, 'scroll')
  const { height: contentHeight } = useResizeObserver(contentRef, 'scroll')
  const allPagesLoaded = useMemo(() => {
    if (!numPages) return false
    let loaded = true
    for (let i = 1; i <= numPages; i++) {
      if (!pageSizes[i]) {
        loaded = false
        break
      }
    }
    return loaded
  }, [numPages, pageSizes])

  const docHeight = useMemo(() => {
    if (!children) return contentHeight
    return contentHeight - headerHeight
  }, [children, contentHeight, headerHeight])

  const docWidth = useMemo(() => Math.max(800, width), [width])
  const viewWidth = useMemo(() => docWidth - (isMobile ? 0 : 30), [docWidth, isMobile])

  const mobileInitialZoom = useMemo(() => {
    if (!isMobile) return 1
    // Calculate zoom to fit page width to viewport, with some padding
    const viewportWidth = width - 20 // Account for padding
    return Math.min(1, viewportWidth / viewWidth)
  }, [isMobile, width, viewWidth])

  const { onDownload, downloadLoading } = usePdfDownload()

  const toBeSignedByMe = useMemo(() => {
    if (!authUser) return false
    if (!document) return false
    if (!isSignableDocument(document)) return false
    return document.toBeSignedByUser === authUser.uid
  }, [authUser, document])

  const isUnsignedConsentForm = useMemo(() => {
    if (!document) return false
    if (!isSignableDocument(document)) return false
    return !document.signedOn
  }, [document])

  return (
    <PdfDocumentFormProvider
      height={height}
      width={modalWidth}
      document={document}
      onFormSubmit={onFormSubmit}
      readOnly={readOnly}>
      <Center zIndex={2} position="relative" w="100%" h="100%" ref={contentRef}>
        {url ? (
          <VStack
            spacing={0}
            width={`${modalWidth}px`}
            height={`${contentHeight}px`}
            position="relative"
            align="flex-start">
            {children ? (
              <Box ref={headerRef} w="100%">
                {children}
              </Box>
            ) : null}
            <Box
              overflow="hidden"
              position="relative"
              width={`${modalWidth}px`}
              height={`${docHeight}px`}>
              {isMobile ? (
                <Box
                  position="relative"
                  overflow="hidden"
                  w="100%"
                  h="100%"
                  style={{
                    touchAction: readOnly ? 'none' : 'auto',
                  }}>
                  <TransformWrapper
                    key={`transform-${mobileInitialZoom}`}
                    initialScale={mobileInitialZoom}
                    minScale={0.5}
                    maxScale={3}
                    limitToBounds={true}
                    centerOnInit={false}
                    centerZoomedOut={false}
                    panning={{
                      disabled: false,
                      excluded: ['input', 'button'],
                    }}
                    wheel={{
                      disabled: true,
                    }}
                    doubleClick={{
                      disabled: true,
                    }}>
                    <TransformComponent
                      contentStyle={{
                        willChange: 'transform',
                      }}
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                      }}>
                      <Box
                        p="10px"
                        ref={scrollRef}
                        position="relative"
                        onClick={() => {
                          onAttachmentSelect(null)
                        }}
                        width="100%"
                        height="100%">
                        <Document
                          inputRef={containerRef}
                          loading={<PdfLoading width={modalWidth} height={docHeight} />}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onPassword={pdfPassword.onPassword}
                          file={url}>
                          {allPagesLoaded ? (
                            pages.map((page, index) => {
                              const pageSize = pageSizes[index + 1]
                              return (
                                <PdfPage
                                  key={`page_${index}`}
                                  page={page}
                                  index={index}
                                  pageWidth={pageSize[0]}
                                  viewWidth={viewWidth}
                                  pageHeight={pageSize[1]}
                                />
                              )
                            })
                          ) : (
                            <PdfLoading width={modalWidth} height={docHeight} />
                          )}
                        </Document>
                      </Box>
                    </TransformComponent>
                  </TransformWrapper>
                  {inputAttachments.length > 0 && toBeSignedByMe && isUnsignedConsentForm ? (
                    <GoToNextField inputIds={inputIds} />
                  ) : null}
                </Box>
              ) : (
                <Box
                  p="10px"
                  ref={scrollRef}
                  overflowY="auto"
                  position="relative"
                  onClick={() => {
                    onAttachmentSelect(null)
                  }}
                  width="100%"
                  height="100%">
                  <Document
                    inputRef={containerRef}
                    loading={<PdfLoading width={modalWidth} height={docHeight} />}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onPassword={pdfPassword.onPassword}
                    file={url}>
                    {allPagesLoaded ? (
                      pages.map((page, index) => {
                        const pageSize = pageSizes[index + 1]
                        if (!pageSize) return null
                        return (
                          <PdfPage
                            key={`page_${index}`}
                            page={page}
                            index={index}
                            pageWidth={pageSize[0]}
                            viewWidth={viewWidth}
                            pageHeight={pageSize[1]}
                          />
                        )
                      })
                    ) : (
                      <PdfLoading width={modalWidth} height={docHeight} />
                    )}
                  </Document>
                  {inputAttachments.length > 0 && toBeSignedByMe && isUnsignedConsentForm ? (
                    <GoToNextField inputIds={inputIds} />
                  ) : null}
                </Box>
              )}
            </Box>
          </VStack>
        ) : (
          <Spinner />
        )}
        {numPages !== null && numPages > 1 ? (
          <PageNumberIndicator containerRef={containerRef} scrollRef={scrollRef} />
        ) : null}
        {withDownload && numPages !== null ? (
          <DocumentDownloadButton
            bottom="unset"
            top={14}
            right={6}
            onDownload={onDownload}
            downloadLoading={downloadLoading}
            document={document}
          />
        ) : null}
        {/* <RegeneratePdfButton withDownload={withDownload} /> */}
        <EnterPassword pdfPassword={pdfPassword} onClose={onClose} />
      </Center>
    </PdfDocumentFormProvider>
  )
}

export const PdfView = ({ onAfterRegenerate, ...props }: PdfViewProps) => (
  <PdfViewProvider
    width={props.width}
    modalWidth={props.modalWidth}
    editable={props.editable}
    attachments={props.attachments}
    document={props.document}
    onAfterRegenerate={onAfterRegenerate}>
    <PdfViewBody {...props} />
  </PdfViewProvider>
)

export type FileValuePdfViewProps = Omit<PdfViewProps, 'url'> & { value: FileDBValue | null }

export const FileValuePdfView = ({ value, ...props }: FileValuePdfViewProps) => {
  const fileArgs = useMemo(() => ({ value, path: value?.storagePath }), [value])
  const { url } = useFile(fileArgs)

  return <PdfView url={url} {...props} />
}
